import { engineComponent, NumberUtils } from "neo-common";
import { ClientHost, Communication, GameEngine, Platforms } from "neo-games";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { BaseActions } from "../Common/Actions/BaseActions";
import { UserControls } from "../Common/UserControls/UserControls";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { GameManager } from "../../Managers/GameManager";
import { Paytable } from "../Common/Paytable/Paytable";
import { GameData } from "../Common/Datas/GameData";
import { GameActions } from "../Common/Actions/GameActions";
import { BaseController } from "../../GameComponents/Base/BaseController";
import { AppContext } from "../../../Common/AppContext";
import { BetManager } from "../../Managers/BetManager";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { BalanceManager } from "../../Managers/BalanceManager";
import { LimitDialog } from "../Common/Dialogs/LimitDialog";
import { ResponsibleDialog1 } from "../Common/Dialogs/ResponsibleDialog1";
import { FreeRoundBonus } from "../Common/Bonuses/FreeRoundBonus";
import { BaseUserControls } from "../Common/UserControls/BaseUserControls";
import { Jackpot } from "../Common/Bonuses/Jackpot";
import { BetHigherThanMinDialog } from "../Common/Dialogs/BetHigherThanMinDialog";
import { FreeRoundBonusDialog } from "../Common/Dialogs/FreeRoundBonusDialog";

@engineComponent(ComponentTypes.HtmlElement)
export class MobileBaseUserControls extends BaseUserControls {
    protected _betSettings: HTMLElement;

    constructor() {
        super();
    }

    create(node: Element): void {
        super.create(node);

        this._betSettings = document.getElementById("betSettings");
        if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
            this._betSettings.classList.add(HtmlController.DisabledClass);
        }
    }

    registerActions(): void {
        super.registerActions();

        if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
            this._betSettings.classList.add(HtmlController.DisabledClass);
        }
    }

    prepareNewGameRound(): void {
        this._afterWinDelay.stop();

        if (this.prepareJackpotMiniGame()) {
            return;
        }

        if (ResponsibleDialog1.opened) {
            return;
        }

        BetHigherThanMinDialog.openDialog();
        Paytable.openAtStart(true);

        if (BetHigherThanMinDialog.canOpenDialog() && FreeRoundBonusDialog.dialogActive()) {
            return;
        }

        if (DataManager.GameLimit) {
            DataManager.Autoplay = false;
            this.limit();
        } else {
            if (!DataManager.Autoplay) {
                this.actions.changeActionStates({
                    [BaseActions.ActionAutoplay]:
                        (DataManager.GameRoundClosed ||
                            (FreeRoundBonus.inFrb &&
                                FreeRoundBonus.winPayMethod === "cumulative" &&
                                !FreeRoundBonus.isLastFrb)) &&
                        DataManager.autoplayEnabled(),
                    [BaseActions.ActionAutoplayStop]: false,
                    [BaseActions.ActionBaseCollect]:
                        FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative"
                            ? false
                            : !DataManager.GameRoundClosed,
                    [BaseActions.ActionBetCycle]: !ModuleManager.gambleEnabled(),
                    [BaseActions.ActionBetPlus]: !BetManager.maxBet,
                    [BaseActions.ActionBetMinus]: !BetManager.minBet,
                    [BaseActions.ActionSpin]: !FreeRoundBonus.isLastFrb,
                    [BaseActions.ActionSpinStop]: false,
                });
                if (this._betSettings != null) {
                    if (!ModuleManager.gambleEnabled()) {
                        if (this._betSettings.classList.contains(HtmlController.DisabledClass)) {
                            this._betSettings.classList.remove(HtmlController.DisabledClass);
                        }
                    } else {
                        if (!this._betSettings.classList.contains(HtmlController.DisabledClass)) {
                            this._betSettings.classList.add(HtmlController.DisabledClass);
                        }
                    }

                    if (FreeRoundBonus.inFrb) {
                        if (!this._betSettings.classList.contains(HtmlController.DisabledClass)) {
                            this._betSettings.classList.add(HtmlController.DisabledClass);
                        }
                    }
                }

                if (DataManager.GameRoundClosed) {
                    Platforms.Platforms.roundInit();
                }
            }
        }

        HtmlController.updateActionElements(this.getAllActions());
    }

    protected prepareJackpotMiniGame(): boolean {
        if (DataManager.GameRoundClosed && Jackpot.hasMiniGame() && !FreeRoundBonus.inFrb) {
            if (Jackpot.hasInitializedGame() && this._wasCalclulated) {
                DataManager.Autoplay = false;
                this.resetWinAnimator();
                this.disable();
                FreeRoundBonus.disableUi();
                Jackpot.startMiniGame();

                return true;
            } else {
                const self = this;

                //in nogamble we need to wait for winCalculated finish to not run minigame right after machinestopped
                if (this._wasCalclulated) {
                    Jackpot.initMiniGame(true, () => {
                        self.resetWinAnimator();
                        self.disable();
                        DataManager.Autoplay = false;
                        FreeRoundBonus.disableUi();
                    });
                }

                //in case we have nogamble and pb within or after gr, check limit
                if (DataManager.GameLimit) {
                    DataManager.Autoplay = false;
                    this.limit();
                }

                if (Jackpot.waitForMiniGameInitialization()) {
                    //in case we have limit, disable all actions during animation and wait for minigame
                    this.actions.disableAllActions();
                    HtmlController.updateActionElements(this.getAllActions());
                    if (
                        this._betSettings != null &&
                        !this._betSettings.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._betSettings.classList.add(HtmlController.DisabledClass);
                    }

                    return true;
                }
            }
        }

        return false;
    }

    limit(): void {
        super.limit();

        if (DataManager.GameRoundClosed) {
            if (this._betSettings != null && this._betSettings.classList.contains(HtmlController.DisabledClass)) {
                this._betSettings.classList.remove(HtmlController.DisabledClass);
            }
        } else {
            if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
                this._betSettings.classList.add(HtmlController.DisabledClass);
            }
        }
    }

    disable(): void {
        super.disable();
        if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
            this._betSettings.classList.add(HtmlController.DisabledClass);
        }
    }

    //disable base without reseting timer and win animations in winAnimator
    //used in frb, to avoid not showing end message in last frb spin with win
    protected disableAllActions(): void {
        this._afterWinDelay.stop();
        this.actions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
            this._betSettings.classList.add(HtmlController.DisabledClass);
        }
    }

    spin(): void {
        this._machineStopped = false;

        FreeRoundBonus.disableUi();
        if (FreeRoundBonus.hasNewActiveOffer()) {
            if (GameManager.gameResponse.totalWin > 0 && !DataManager.GameRoundClosed) {
                this.changeWinBox(true);
                this.collect();
            } else {
                FreeRoundBonus.enableUi();
            }
            return;
        }

        if (this.checkJackpotMiniGame()) {
            return;
        }

        if (
            ResponsibleDialog1.showResponsibleDialog1OnCollect &&
            !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
        ) {
            if (GameManager.gameResponse.totalWin > 0) {
                this.changeWinBox(true);
                this.collect();
            } else {
                GameManager.canExit();
                this.changeWinBox();
            }

            return;
        }

        if (BetHigherThanMinDialog.canOpenDialog()) {
            if (GameManager.gameResponse.totalWin > 0) {
                this.changeWinBox(true);
                this.collect();
            }
            BetHigherThanMinDialog.openDialog();
            return;
        }

        if (Paytable.shouldOpen(true, true)) {
            if (GameManager.gameResponse.totalWin > 0) {
                this.changeWinBox(true);
                this.collect();
            } else {
                Paytable.show();
            }
            return;
        }

        if (
            !DataManager.Autoplay &&
            !this._wasCalclulated &&
            !DataManager.GameRoundClosed &&
            AppContext.gameConfiguration.CollectOnUnCalculatedWin
        ) {
            this.changeWinBox(true);
            this.collect();
            return;
        } else {
            this.changeWinBox();
            if (!this._wasCalclulated) {
                if (GameManager.baseResponse != null && GameManager.baseResponse.currentWin > 0) {
                    Platforms.Platforms.win(GameManager.baseResponse.currentWin);
                }
            }
        }
        if (GameManager.gameResponse.totalWin > 0 && !DataManager.GameRoundClosed) {
            const balanceTotal = parseFloat(DataManager.getDataById(GameData.DataBalanceTotal));
            const balanceAfter = parseFloat(NumberUtils.add(GameManager.gameResponse.totalWin, balanceTotal));
            Platforms.Platforms.roundEnded({
                id: GameManager.gameResponse.roundId,
                win: GameManager.gameResponse.totalWin,
                balanceAfter: balanceAfter,
                inSpin: true,
            });
        }

        if (!Platforms.Platforms.canSpinOnCollect) {
            this.autoplayStop();
            this.changeWinBox(true);
            this.collect();
            return;
        }

        if (!BalanceManager.hasCredit(false, true)) {
            DataManager.Autoplay = false;
            ModuleManager.disableGamble();
            this.prepareNewGameRound();
            return;
        }

        DataManager.Collectable = false;
        ModuleManager.disableGamble();
        this.actions.disableAllActions();
        if (this._betSettings != null && !this._betSettings.classList.contains(HtmlController.DisabledClass)) {
            this._betSettings.classList.add(HtmlController.DisabledClass);
        }
        if (DataManager.Autoplay) {
            if (
                !DataManager.AutoplayStarted &&
                GameManager.baseResponse != null &&
                GameManager.baseResponse.totalWin != null &&
                GameManager.baseResponse.totalWin > 0
            ) {
                DataManager.updateLossLimit(GameManager.baseResponse.totalWin);
            }
            DataManager.updateLossLimit();
            this.actions.changeActionState(BaseActions.ActionAutoplayStop, DataManager.autoplayEnabled());
        }
        HtmlController.dispatchUiAction("");
        HtmlController.updateActionElements(this.getAllActions());
        this.playCollectSound();

        DataManager.updateTotalGain(true);
        BalanceManager.fixBalance();
        if (!DataManager.GameRoundClosed || (FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
            DataManager.updateDataElement(GameData.DataTotalWin);
        }
        this._controller.spin();

        LimitDialog.closeDialog();

        this._wasCalclulated = false;

        Platforms.Platforms.roundStarted({ totalBet: BetManager.Bet });
        Platforms.Platforms.spinStarted();
        Platforms.Platforms.balanceUpdated({ amount: parseFloat(DataManager.getDataById(GameData.DataBalanceTotal)) });
    }

    introFinished(): void {
        if (DataManager.GameRoundClosed) {
            Platforms.Platforms.roundEnded();
        }
        if (!ResponsibleDialog1.opened) {
            DataManager.Autoplay = DataManager.AutoplayAfterGamble;
        }
        ModuleManager.enableGamble();
        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            this.autoplay();
        }

        this._controlsAfterGambleDelay.start(0);

        if (
            this.longIntro &&
            DataManager.GameRoundClosed &&
            (FreeRoundBonus.id != null || FreeRoundBonus.otherOfferes.length > 0)
        ) {
            FreeRoundBonus.enableUi();
            this.longIntro = false;
        }

        if (FreeRoundBonus.isLastFrb && DataManager.GameRoundClosed) {
            FreeRoundBonus.endFreeRounds();
            ModuleManager.disableBase();
            return;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Mobile/MobileBaseUserControls.ts
 **/